import tw from 'twin.macro'
import React, { useState } from 'react'

import * as Popover from '@radix-ui/react-popover'
import { useForm, FormProvider } from 'react-hook-form'
import { CgImage, CgTrash } from 'react-icons/cg'

import { useListenExistingItemDraftsQuery } from 'modules/Uploads/uploadApi'

import { Tooltip } from 'common/components/Tooltip/Tooltip'
import { UploadInput } from 'common/components/UploadInput/UploadInput'
import { Spinner } from 'common/components/Spinner/Spinner'
import { isUploadingAssets } from 'common/utils/fileUploading/uploadUtils'

function AnswerImgUpload({ questionId, answerId, answerTitle, answerImg, coachOrgId, updateAnswerImg }) {
  const { data: assetDrafts } = useListenExistingItemDraftsQuery({ coachOrgId, id: answerId })
  const isUploading = isUploadingAssets(assetDrafts)

  const [isImgPopoverOpen, setIsImgPopoverOpen] = useState(false)

  const defaultValues = {
    image: null,
  }

  const methods = useForm({
    defaultValues,
  })

  const {
    setValue,
    setError,
    clearErrors,
    register,
    formState: { errors },
  } = methods

  return (
    <div css={[actionContainerClasses, isImgPopoverOpen && tw`visible`]} className='nopan nodrag'>
      <Tooltip
        content={isUploading ? 'Image uploading' : 'Upload image'}
        triggerClasses='flex items-center justify-center cursor-pointer'
      >
        <Popover.Root open={isImgPopoverOpen} onOpenChange={setIsImgPopoverOpen}>
          <Popover.Trigger
            asChild={true}
            onClick={(e) => {
              e.stopPropagation()
              setIsImgPopoverOpen(true)
            }}
          >
            <button tabIndex={-1} className='flex items-center' onClick={(e) => e.stopPropagation()}>
              {isUploading ? (
                <Spinner className='w-4 h-4 text-gray-100' />
              ) : (
                <CgImage
                  className='w-4 h-4 text-gray-500 hover:text-tGreen transition-colors'
                  css={Boolean(answerImg) && tw`text-tGreen`}
                />
              )}
            </button>
          </Popover.Trigger>
          <Popover.Portal>
            <Popover.Content
              align='end'
              alignOffset={-7}
              sideOffset={6}
              className='bg-white px-4 py-4 rounded-xl w-96 shadow-2xl border-2 border-gray-300 z-[999]'
              onClick={(e) => e.stopPropagation()}
              arrowPadding={12}
            >
              <Popover.Arrow className='fill-gray-300' />
              <div className='flex justify-between mb-7'>
                <h4 className='font-bold text-tBlack capitalize self-center'>
                  {answerTitle ? answerTitle : 'Untitled answer'}
                </h4>
                <Popover.Close css={popoverBtnClasses}>Close</Popover.Close>
              </div>
              <FormProvider {...methods}>
                <form>
                  <div className='flex items-center'>
                    <div className='flex-1'>
                      <UploadInput
                        label='Upload image'
                        name='image'
                        id={answerId}
                        questionId={questionId}
                        coachOrgId={coachOrgId}
                        register={register}
                        setValue={setValue}
                        liveUrl={answerImg}
                        uploadType='quiz-answer-image'
                        setError={setError}
                        clearErrors={clearErrors}
                        fileType='image'
                        previewIcon={<CgImage className='!w-5 !h-5' />}
                      />
                    </div>
                    {Boolean(answerImg) && (
                      <div className='w-10 h-10 ml-1 flex items-center justify-center'>
                        <Tooltip content='Delete image' triggerClasses='flex'>
                          <button
                            tabIndex={-1}
                            onClick={() => {
                              setValue('image', null)
                              updateAnswerImg(null)
                            }}
                            type='button'
                          >
                            <CgTrash className='w-6 h-6 text-tGray-med hover:text-tRed-dark' />
                          </button>
                        </Tooltip>
                      </div>
                    )}
                  </div>
                  {errors?.image ? <p className='flex text-xs mt-1 text-tRed'>{errors.image.message}</p> : null}
                </form>
              </FormProvider>
              {isUploading && (
                <p className='text-xs text-gray-500 mt-4'>
                  * Feel free to close this box while the image is being uploaded.
                </p>
              )}
            </Popover.Content>
          </Popover.Portal>
        </Popover.Root>
      </Tooltip>
    </div>
  )
}

export default AnswerImgUpload

const popoverBtnClasses = tw`
  text-tGreen rounded-lg px-2 py-1
  hover:bg-tGreen hover:bg-opacity-10 transition-all whitespace-nowrap ml-4 self-start
`
const actionContainerClasses = tw`
  invisible absolute z-20 
  right-1 top-1 flex 
  items-center py-0.5 px-1 
  rounded border-[1px] bg-white 
  group-hover:visible hover:visible 
  border-gray-200 shadow-sm
`
